/**
 * ConsentBanner — first-visit analytics consent prompt.
 *
 * Default state: strictly-necessary only (no analytics fires).
 * Choice is recorded in localStorage under `guild.consent` as:
 *   { analytics: boolean, version: number, ts: number }
 *
 * The analytics util (`src/utils/analytics.js`) reads this flag before
 * firing GA4 events or the server-side `recordAnalyticsEvent` callable.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const CONSENT_KEY = "guild.consent";
const CONSENT_VERSION = 1;

function readStored() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch (_e) {
    return null;
  }
}

function writeStored(analytics) {
  try {
    localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify({
        analytics: !!analytics,
        version: CONSENT_VERSION,
        ts: Date.now(),
      })
    );
  } catch (_e) {
    /* ignore */
  }
}

export default function ConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Defer the read so SSR / no-storage browsers fail silently.
    if (!readStored()) setVisible(true);
  }, []);

  if (!visible) return null;

  const accept = () => {
    writeStored(true);
    setVisible(false);
  };
  const reject = () => {
    writeStored(false);
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9000,
        background: "rgba(20, 18, 14, 0.96)",
        color: "#f7efe1",
        borderTop: "1px solid rgba(255,255,255,0.12)",
        padding: "1rem 1.25rem",
        boxShadow: "0 -8px 24px rgba(0,0,0,0.3)",
      }}
    >
      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          display: "flex",
          flexWrap: "wrap",
          gap: "1rem",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <p style={{ margin: 0, fontSize: "0.92rem", maxWidth: 720 }}>
          Ahoy! We use a few strictly-necessary cookies to keep ye signed in,
          and (if ye allow) anonymous analytics to learn what's workin'. No
          spam, no sellin' yer secrets — ye have our word. See the{" "}
          <Link to="/cookies" style={{ color: "#ffcc6a" }}>
            Cookie Policy
          </Link>{" "}
          and{" "}
          <Link to="/privacy" style={{ color: "#ffcc6a" }}>
            Privacy Policy
          </Link>
          .
        </p>
        <div style={{ display: "flex", gap: "0.5rem", flexShrink: 0 }}>
          <button
            type="button"
            onClick={reject}
            className="marketing-cta"
            style={{ background: "transparent", color: "#f7efe1" }}
          >
            Strictly necessary only
          </button>
          <button
            type="button"
            onClick={accept}
            className="marketing-cta marketing-cta--bright"
          >
            Allow analytics
          </button>
        </div>
      </div>
    </div>
  );
}
